import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  fetchLiveTradingStatsSummary,
  fetchLiveTradingStatsTradeDetail,
  fetchLiveTradingStatsTrades,
} from '../../api/liveTradingStats';

const COLORS = {
  ink: '#17323c',
  muted: '#4f646f',
  success: '#2f7d5d',
  danger: '#9c4f4f',
  warning: '#b57422',
  line: '#d8e2e7',
};

const STATUS_OPTIONS = [
  ['', 'Tous'],
  ['INTENT_RECORDED', 'Intention créée'],
  ['SUBMITTING', 'Envoi en cours'],
  ['REJECTED', 'Rejeté'],
  ['FILL_UNKNOWN', 'Fill inconnu'],
  ['ACKNOWLEDGED', 'Acquitté sans fill'],
  ['PARTIALLY_FILLED', 'Entrée partielle'],
  ['FILLED', 'Exécuté'],
  ['POSITION_CLOSED', 'Position clôturée'],
];

const VIEW_OPTIONS = [
  ['ALL', 'Toutes les intentions'],
  ['OPEN', 'Positions ouvertes'],
  ['CLOSED', 'Trades clôturés'],
  ['NOT_OPENED', 'Intentions non exécutées'],
  ['EXECUTED', 'Trades exécutés'],
  ['UNKNOWN', 'Statut inconnu'],
];

const STATUS_COLORS = {
  OPEN: 'success',
  CLOSED: 'default',
  NOT_OPENED: 'warning',
  UNKNOWN: 'warning',
};

function dateText(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function timestampText(value) {
  if (value === null || value === undefined || value === '') return '—';
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) return '—';
  return dateText(new Date(timestamp).toISOString());
}

function amountText(money, fallback = '—') {
  if (!money || !money.available || money.amount === null || money.amount === undefined) return fallback;
  const numeric = Number(money.amount);
  if (!Number.isFinite(numeric)) return fallback;
  const text = numeric.toLocaleString('fr-FR', { maximumFractionDigits: 8 });
  return `${text}${money.currency ? ` ${money.currency}` : ''}`;
}

function countText(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('fr-FR') : '0';
}

function countOrDash(value) {
  if (value === null || value === undefined) return '—';
  return countText(value);
}

function percentText(rate) {
  if (!rate || !rate.available) return '—';
  return `${(Number(rate.value) * 100).toFixed(1)} %`;
}

function MoneyWithReason({ money, fallback = '—' }) {
  if (!money || !money.available) {
    return (
      <Tooltip title={money?.reason || 'Donnée indisponible'}>
        <span tabIndex={0}>{fallback}</span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={money.complete ? 'Montant complet' : (money.reason || 'Montant partiel')}>
      <span tabIndex={0}>{amountText(money)}</span>
    </Tooltip>
  );
}

function SummaryCard({ label, value, hint, tone = 'default' }) {
  const color = tone === 'success' ? COLORS.success : tone === 'danger' ? COLORS.danger : COLORS.ink;
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 700, textTransform: 'uppercase' }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ color, fontWeight: 900, mt: 0.25 }}>{value}</Typography>
        {hint ? <Typography variant="caption" sx={{ color: COLORS.muted }}>{hint}</Typography> : null}
      </CardContent>
    </Card>
  );
}

function PnlHistoryChart({ points }) {
  if (!points?.length) {
    return <Alert severity="info">Aucun P&L connu sur des positions clôturées.</Alert>;
  }
  const values = points.map((point) => Number(point.cumulativeKnownGrossPnl));
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const span = max - min || 1;
  const width = 620;
  const height = 180;
  const padding = 20;
  const coords = points.map((point, index) => {
    const x = points.length === 1
      ? width / 2
      : padding + (index * (width - padding * 2)) / (points.length - 1);
    const y = height - padding - ((Number(point.cumulativeKnownGrossPnl) - min) / span) * (height - padding * 2);
    return `${x},${y}`;
  });
  return (
    <Box>
      <Box component="svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Évolution du P&L brut connu" sx={{ width: '100%', height: 200 }}>
        <line x1={padding} x2={width - padding} y1={height - padding} y2={height - padding} stroke={COLORS.line} />
        <polyline fill="none" stroke={COLORS.ink} strokeWidth="3" points={coords.join(' ')} />
        {coords.map((coord, index) => {
          const [x, y] = coord.split(',').map(Number);
          return <circle key={`${points[index].at}-${index}`} cx={x} cy={y} r="4" fill={COLORS.ink} />;
        })}
      </Box>
      <Typography variant="caption" sx={{ color: COLORS.muted }}>
        P&L brut connu, TP fills uniquement. Les frais d’entrée, le financement et les clôtures SL/manuelles ne sont pas inclus.
      </Typography>
    </Box>
  );
}

function TpFrequencyChart({ rows }) {
  return (
    <Stack spacing={1.25}>
      {rows.map((row) => {
        const total = Math.max(1, row.openedPositions);
        return (
          <Box key={row.level}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="body2" sx={{ fontWeight: 800 }}>{row.level}</Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted }}>
                {row.full} complets · {row.partial} partiels · {row.placed} placés / {row.openedPositions}
              </Typography>
            </Stack>
            <Box sx={{ display: 'flex', height: 12, borderRadius: 99, overflow: 'hidden', bgcolor: '#edf2f4', mt: 0.4 }}>
              <Box sx={{ width: `${(row.full / total) * 100}%`, bgcolor: COLORS.success }} />
              <Box sx={{ width: `${(row.partial / total) * 100}%`, bgcolor: COLORS.warning }} />
              <Box sx={{ width: `${(row.placed / total) * 100}%`, bgcolor: COLORS.line }} />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function BreakdownBars({ rows, emptyText }) {
  if (!rows?.length) return <Alert severity="info">{emptyText}</Alert>;
  const max = Math.max(1, ...rows.map((row) => Math.abs(Number(row.knownGrossPnl?.amount || 0))));
  return (
    <Stack spacing={1}>
      {rows.map((row) => {
        const amount = Number(row.knownGrossPnl?.amount || 0);
        const width = (Math.abs(amount) / max) * 100;
        return (
          <Box key={row.key}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.key}</Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted }}>{row.trades} trade(s)</Typography>
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
              <Box sx={{ flex: 1, bgcolor: '#edf2f4', borderRadius: 99, height: 8, overflow: 'hidden' }}>
                <Box sx={{ width: `${width}%`, height: '100%', bgcolor: amount < 0 ? COLORS.danger : COLORS.success }} />
              </Box>
              <Typography variant="caption" sx={{ minWidth: 90, textAlign: 'right', color: amount < 0 ? COLORS.danger : COLORS.success }}>
                {amount === 0 ? '—' : amount.toLocaleString('fr-FR', { maximumFractionDigits: 8 })}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function TpCell({ leg }) {
  if (!leg || (!leg.placed && !leg.targetPrice)) return <span>—</span>;
  const label = leg.full ? 'E' : leg.partial ? 'P' : leg.placed ? 'Placé' : 'Prévu';
  const color = leg.full ? 'success' : leg.partial ? 'warning' : 'default';
  return (
    <Tooltip title={`${leg.level} · cible ${leg.targetPrice ?? '—'} · exécuté ${leg.filledContracts ?? 0}/${leg.plannedContracts ?? '—'} · état ${leg.orderState || '—'}`}>
      <Stack spacing={0.25} alignItems="flex-start" tabIndex={0}>
        <Chip size="small" color={color} label={label} sx={{ height: 20, fontWeight: 700 }} />
        <Typography variant="caption" sx={{ color: COLORS.muted }}>
          {leg.filledContracts ?? 0}/{leg.plannedContracts ?? '—'}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

function TradeTable({ rows, onDetail, loading }) {
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>;
  }
  if (!rows?.length) {
    return <Alert severity="info">Aucun trade LIVE pour ces filtres.</Alert>;
  }
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, maxHeight: 620 }}>
      <Table size="small" sx={{ minWidth: 1550 }}>
        <TableHead>
          <TableRow>
            {['Ouverture', 'Exchange', 'Instrument', 'Direction', 'Qté', 'Entrée', 'SL initial', 'Dernier SL',
              'TP1', 'TP2', 'TP3', 'Restant', 'Statut actuel', 'P&L brut TP', 'Net', 'Frais connus', 'Clôture', ''].map((label) => (
              <TableCell key={label} sx={{ fontWeight: 800, bgcolor: '#f7fafb', whiteSpace: 'nowrap' }}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const byLevel = Object.fromEntries((row.takeProfits || []).map((leg) => [leg.level, leg]));
            return (
              <TableRow key={row.intentId} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateText(row.openedAt)}</TableCell>
                <TableCell>{row.exchange}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{row.instrument}</TableCell>
                <TableCell>
                  <Chip size="small" label={row.direction} color={row.direction === 'LONG' ? 'success' : 'error'} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.filledContracts ?? '—'} / {row.requestedContracts ?? '—'}</TableCell>
                <TableCell>{row.averageEntryPrice ?? '—'}</TableCell>
                <TableCell>{row.initialStopLoss ?? '—'}</TableCell>
                <TableCell>{row.lastStopLoss ?? '—'}</TableCell>
                <TableCell><TpCell leg={byLevel.TP1} /></TableCell>
                <TableCell><TpCell leg={byLevel.TP2} /></TableCell>
                <TableCell><TpCell leg={byLevel.TP3} /></TableCell>
                <TableCell>{row.remainingContracts ?? '—'}</TableCell>
                <TableCell>
                  <Tooltip title={`${row.positionReason || row.positionStatus} · lecture ${dateText(row.positionReadAt)}`}>
                    <Chip size="small" label={row.positionStatus} color={STATUS_COLORS[row.positionStatus] || 'default'} />
                  </Tooltip>
                </TableCell>
                <TableCell><MoneyWithReason money={row.knownGrossPnl} /></TableCell>
                <TableCell><MoneyWithReason money={row.netPnl} /></TableCell>
                <TableCell><MoneyWithReason money={row.knownFees} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateText(row.closedAt)}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => onDetail(row.intentId)}>Détails</Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function TradeDetailDialog({ open, detail, loading, error, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Détail du trade LIVE</DialogTitle>
      <DialogContent dividers>
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {detail ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={detail.trade.instrument} color="primary" />
              <Chip label={detail.trade.direction} color={detail.trade.direction === 'LONG' ? 'success' : 'error'} />
              <Chip label={detail.trade.positionStatus} />
              <Chip label={`Ouvert ${dateText(detail.trade.openedAt)}`} variant="outlined" />
              <Chip label={`Clôturé ${dateText(detail.trade.closedAt)}`} variant="outlined" />
              <Chip label={`Clôture: ${detail.trade.closureReason || '—'}`} variant="outlined" />
            </Stack>
            <Alert severity="warning">
              P&L brut partiel calculé uniquement sur les fills TP persistés. Frais d’entrée, funding,
              SL et clôtures manuelles ne sont pas persistés dans ce périmètre.
            </Alert>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>TP</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Niveau', 'Cible', 'Prévu', 'Placé', 'État', 'Exécuté', 'Prix moyen', 'Frais', 'Statut'].map((label) => (
                      <TableCell key={label} sx={{ fontWeight: 800 }}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.takeProfits.map((leg) => (
                    <TableRow key={leg.level}>
                      <TableCell>{leg.level}</TableCell>
                      <TableCell>{leg.targetPrice ?? '—'}</TableCell>
                      <TableCell>{leg.plannedContracts ?? '—'}</TableCell>
                      <TableCell>{leg.placed ? 'Oui' : 'Non'}</TableCell>
                      <TableCell>{leg.orderState || '—'}</TableCell>
                      <TableCell>{leg.filledContracts ?? '—'}</TableCell>
                      <TableCell>{leg.averageFillPrice ?? '—'}</TableCell>
                      <TableCell><MoneyWithReason money={leg.knownFees} /></TableCell>
                      <TableCell>{leg.full ? 'Complet' : leg.partial ? 'Partiel' : 'Non exécuté'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Fills</Typography>
            {detail.fills.length ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Niveau', 'Order', 'Trade', 'Prix', 'Taille', 'Frais', 'Timestamp'].map((label) => (
                        <TableCell key={label} sx={{ fontWeight: 800 }}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.fills.map((fill) => (
                      <TableRow key={`${fill.orderId}-${fill.tradeId}`}>
                        <TableCell>{fill.level}</TableCell>
                        <TableCell>{fill.orderId}</TableCell>
                        <TableCell>{fill.tradeId}</TableCell>
                        <TableCell>{fill.fillPrice}</TableCell>
                        <TableCell>{fill.fillSize}</TableCell>
                        <TableCell>{fill.fee ?? '—'}</TableCell>
                        <TableCell>{timestampText(fill.fillTs)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : <Alert severity="info">Aucun fill TP persisté.</Alert>}
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Chronologie</Typography>
            {detail.events.length ? (
              <Stack spacing={1}>
                {detail.events.map((event) => (
                  <Stack key={event.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <Typography variant="caption" sx={{ color: COLORS.muted, minWidth: 150 }}>{dateText(event.createdAt)}</Typography>
                    <Chip size="small" label={event.eventType} />
                    <Typography variant="body2">{event.detail || '—'}</Typography>
                  </Stack>
                ))}
              </Stack>
            ) : <Alert severity="info">Aucun événement persisté.</Alert>}
          </Stack>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function MobileCards({ rows, onDetail }) {
  if (!rows?.length) return <Alert severity="info">Aucun trade LIVE pour ces filtres.</Alert>;
  return (
    <Stack spacing={1.25}>
      {rows.map((row) => (
        <Card key={row.intentId} variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>{row.instrument}</Typography>
                <Typography variant="caption" sx={{ color: COLORS.muted }}>{dateText(row.openedAt)} · {row.exchange}</Typography>
              </Box>
              <Chip size="small" label={row.direction} color={row.direction === 'LONG' ? 'success' : 'error'} />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={row.positionStatus} />
              <Chip size="small" label={`Entrée ${row.averageEntryPrice ?? '—'}`} />
              <Chip size="small" label={`Qté ${row.filledContracts ?? '—'}`} />
              <Chip size="small" label={`TP1 ${row.takeProfits?.find((leg) => leg.level === 'TP1')?.full ? 'E' : '—'}`} />
              <Chip size="small" label={`TP2 ${row.takeProfits?.find((leg) => leg.level === 'TP2')?.full ? 'E' : '—'}`} />
              <Chip size="small" label={`TP3 ${row.takeProfits?.find((leg) => leg.level === 'TP3')?.full ? 'E' : '—'}`} />
            </Stack>
            <Typography variant="body2" sx={{ mt: 1 }}>
              P&L brut : <MoneyWithReason money={row.knownGrossPnl} />
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              Net : <MoneyWithReason money={row.netPnl} /> · Frais : <MoneyWithReason money={row.knownFees} />
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Button size="small" onClick={() => onDetail(row.intentId)}>Détails</Button>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

export default function TradingStatsPage() {
  const [draft, setDraft] = useState({
    exchange: 'BLOFIN', instrument: '', direction: '', status: '', from: '', to: '', view: 'ALL',
  });
  const [filters, setFilters] = useState(draft);
  const [summary, setSummary] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const requestFilters = useMemo(() => ({
    exchange: filters.exchange || undefined,
    instrument: filters.instrument || undefined,
    direction: filters.direction || undefined,
    status: filters.status || undefined,
    from: filters.from ? `${filters.from}T00:00:00` : undefined,
    to: filters.to ? `${filters.to}T23:59:59` : undefined,
    view: filters.view || 'ALL',
  }), [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [nextSummary, nextPage] = await Promise.all([
        fetchLiveTradingStatsSummary(requestFilters),
        fetchLiveTradingStatsTrades({ ...requestFilters, page, size: 25 }),
      ]);
      setSummary(nextSummary);
      setPageData(nextPage);
    } catch (loadError) {
      setError(loadError.message || 'Impossible de charger les statistiques LIVE.');
      setSummary(null);
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, [page, requestFilters]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = () => {
    setPage(0);
    setFilters({ ...draft });
  };

  const resetFilters = () => {
    const next = { exchange: 'BLOFIN', instrument: '', direction: '', status: '', from: '', to: '', view: 'ALL' };
    setDraft(next);
    setFilters(next);
    setPage(0);
  };

  const openDetail = async (intentId) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      setDetail(await fetchLiveTradingStatsTradeDetail(intentId));
    } catch (loadError) {
      setDetailError(loadError.message || 'Détail indisponible.');
    } finally {
      setDetailLoading(false);
    }
  };

  const rows = pageData?.rows || [];
  const totalPages = pageData?.totalPages || 0;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1700, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, color: COLORS.ink }}>Statistiques trading LIVE</Typography>
          <Typography variant="body2" sx={{ color: COLORS.muted }}>
            Lecture seule des intentions, fills TP et états persistés. Aucun appel exchange, aucune réconciliation, aucune écriture.
          </Typography>
        </Box>
        <Button startIcon={<RefreshRoundedIcon />} variant="outlined" onClick={load} disabled={loading}>Actualiser</Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap" alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 145 }}>
            <InputLabel>Exchange</InputLabel>
            <Select label="Exchange" value={draft.exchange} onChange={(event) => setDraft({ ...draft, exchange: event.target.value })}>
              <MenuItem value="BLOFIN">BloFin</MenuItem>
              <MenuItem value="">Tous</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="Instrument" value={draft.instrument}
            onChange={(event) => setDraft({ ...draft, instrument: event.target.value })} sx={{ minWidth: 160 }} />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Direction</InputLabel>
            <Select label="Direction" value={draft.direction} onChange={(event) => setDraft({ ...draft, direction: event.target.value })}>
              <MenuItem value="">Toutes</MenuItem>
              <MenuItem value="LONG">LONG</MenuItem>
              <MenuItem value="SHORT">SHORT</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Vue</InputLabel>
            <Select
              label="Vue"
              value={draft.view}
              onChange={(event) => setDraft({ ...draft, view: event.target.value })}
              inputProps={{ 'aria-label': 'Vue' }}
            >
              {VIEW_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 175 }}>
            <InputLabel>Statut local</InputLabel>
            <Select label="Statut local" value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })}>
              {STATUS_OPTIONS.map(([value, label]) => <MenuItem key={value || 'all'} value={value}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" type="date" label="Du" value={draft.from}
            onChange={(event) => setDraft({ ...draft, from: event.target.value })} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" label="Au" value={draft.to}
            onChange={(event) => setDraft({ ...draft, to: event.target.value })} InputLabelProps={{ shrink: true }} />
          <Button variant="contained" onClick={applyFilters} sx={{ bgcolor: COLORS.ink }}>Filtrer</Button>
          <Button onClick={resetFilters}>Réinitialiser</Button>
        </Stack>
      </Paper>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {summary ? (
        <Alert
          severity={summary.positionSync?.state === 'SUCCESS' ? 'info' : 'warning'}
          icon={false}
          sx={{ mb: 2 }}
        >
          {summary.positionSync?.state === 'SUCCESS'
            ? `Positions synchronisées en lecture seule depuis BloFin à ${dateText(summary.positionSync.readAt)}. `
            : `Synchronisation des positions indisponible (${summary.positionSync?.reason || 'raison inconnue'}). Les statuts OPEN/CLOSED ne sont pas affirmés. `}
          Le P&L affiché reste un P&L brut partiel fondé sur les fills TP persistés. Frais d’entrée, funding,
          SL et clôtures manuelles ne sont pas encore persistés : le P&L net et le win rate restent indisponibles.
        </Alert>
      ) : null}

      {summary ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))' }, gap: 1.25, mb: 2 }}>
            <SummaryCard label="Intentions" value={countText(summary.intentionsCreated)} hint="Historique conservé" />
            <SummaryCard label="Trades exécutés" value={countText(summary.executedPositions ?? summary.positionsOpened)} hint={`${countText(summary.notOpenedPositions)} non exécutée(s)`} />
            <SummaryCard label="Positions ouvertes" value={countOrDash(summary.openPositions)} hint="Lecture BloFin" tone="success" />
            <SummaryCard label="Trades clôturés" value={countOrDash(summary.closedPositions)} hint="Positions distantes absentes" />
            <SummaryCard label="Statut inconnu" value={countText(summary.unknownPositions)} hint="Synchronisation insuffisante" />
            <SummaryCard label="P&L brut TP" value={amountText(summary.knownGrossPnl)} hint="Partiel, fills TP seulement" tone="success" />
            <SummaryCard label="P&L total réalisé" value={amountText(summary.totalRealizedPnl)} hint="Sorties complètes requises" tone="danger" />
            <SummaryCard label="P&L latent" value={amountText(summary.latentPnl)} hint="Prix distant fiable requis" />
            <SummaryCard label="P&L net" value={amountText(summary.netPnl)} hint="Indisponible" tone="danger" />
            <SummaryCard label="Frais connus" value={amountText(summary.knownFees)} hint="Partiels, hors frais d’entrée" />
            <SummaryCard label="Win rate" value={percentText(summary.winRate)} hint="P&L complet requis" />
            <SummaryCard label="Gain moyen" value={amountText(summary.averageWin)} />
            <SummaryCard label="Perte moyenne" value={amountText(summary.averageLoss)} />
            <SummaryCard label="TP1 complets" value={countText(summary.tp1Full)} hint={`${summary.tp1Partial} partiel(s)`} tone="success" />
            <SummaryCard label="TP2 complets" value={countText(summary.tp2Full)} hint={`${summary.tp2Partial} partiel(s)`} tone="success" />
            <SummaryCard label="TP3 complets" value={countText(summary.tp3Full)} hint={`${summary.tp3Partial} partiel(s)`} tone="success" />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr 1fr' }, gap: 1.5, mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Évolution du P&L brut connu</Typography>
              <PnlHistoryChart points={summary.realizedPnlHistory} />
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Fréquence TP</Typography>
              <TpFrequencyChart rows={summary.takeProfits || []} />
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" sx={{ color: COLORS.muted }}>
                Gagnants/perdants connus : {summary.winLossDistribution?.winners ?? 0}/{summary.winLossDistribution?.losers ?? 0}
                {summary.winLossDistribution?.complete ? '' : ' (base partielle)'}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>LONG / SHORT</Typography>
              <BreakdownBars rows={summary.byDirection || []} emptyText="Aucun résultat." />
            </Paper>
          </Box>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Performance par instrument</Typography>
            <BreakdownBars rows={summary.byInstrument || []} emptyText="Aucun instrument." />
          </Paper>
        </>
      ) : null}

      <Paper variant="outlined" sx={{ p: { xs: 1, md: 2 }, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>Historique des intentions et des trades</Typography>
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              Toutes les intentions sont conservées, y compris celles qui n’ont jamais été exécutées.
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: COLORS.muted }}>{pageData ? `${pageData.totalElements} ligne(s)` : ''}</Typography>
        </Stack>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TradeTable rows={rows} onDetail={openDetail} loading={loading} />
        </Box>
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {loading ? <CircularProgress /> : <MobileCards rows={rows} onDetail={openDetail} />}
        </Box>
        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ mt: 1.5 }}>
          <Button disabled={page <= 0 || loading} onClick={() => setPage((current) => Math.max(0, current - 1))}>Précédent</Button>
          <Typography variant="caption" sx={{ color: COLORS.muted }}>
            Page {totalPages === 0 ? 0 : page + 1} / {totalPages}
          </Typography>
          <Button disabled={loading || page + 1 >= totalPages} onClick={() => setPage((current) => current + 1)}>Suivant</Button>
        </Stack>
      </Paper>

      <TradeDetailDialog
        open={detailOpen}
        detail={detail}
        loading={detailLoading}
        error={detailError}
        onClose={() => setDetailOpen(false)}
      />
    </Box>
  );
}


